import { ChangeDetectionStrategy, Component, computed, Inject, inject, OnInit, signal, Signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { themeEstensa } from '@se/se.platform.base';
import { AgGridModule } from 'ag-grid-angular';
import {
    AllCommunityModule,
    ColDef,
    FirstDataRenderedEvent,
    GridApi,
    GridSizeChangedEvent,
    RowSelectionOptions,
    SelectionChangedEvent,
    SizeColumnsToFitGridStrategy
} from 'ag-grid-community';
import EstensaModel from 'estensa-module';
import { sumBy } from 'lodash-es';
import moment, { Moment } from 'moment';
import { BrokerService } from '../../../services/angularjs/broker.service';
import { BrokerAssociationStatus, BrokerExtendedTableDataDisplay } from '../../broker.models';
import { BrokerAssocSignalStore } from './broker-assoc.store';
import { StatusRendererComponent } from './status-renderer.component';

const MY_FORMATS = {
    parse: {
        dateInput: 'DD/MM/YYYY', // this is how your date will be parsed from Input
    },
    display: {
        dateInput: 'DD/MM/YYYY', // this is how your date will get displayed on the Input
        monthYearLabel: 'MMMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
    selector: 'app-broker-association-modal',
    templateUrl: './broker-association-modal.component.html',
    styleUrls: ['./broker-association-modal.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        MatDialogConfig,
        BrokerAssocSignalStore,
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    ],
    imports: [
        AgGridModule,
        MatCardModule,
        MatButtonModule,
        MatDialogModule,
        MatDatepickerModule,
        MatIconModule,
        MatInputModule,
        MatFormFieldModule,
        MatSelectModule,
        MatTooltipModule,
        TranslateModule,
        ReactiveFormsModule,
    ],
})
export class BrokerAssosiationModalComponent implements OnInit {
    brokerAssocStore = inject(BrokerAssocSignalStore);
    translateService = inject(TranslateService);
    brokerService = inject(BrokerService);

    agGridModuls = [AllCommunityModule];
    agGridTheme = themeEstensa;

    sexes = this.brokerAssocStore.sexes;
    modalities = this.brokerAssocStore.modalities;
    typologies = this.brokerAssocStore.typologies;
    studies = this.brokerAssocStore.studies;
    studyFilterSearch = this.brokerAssocStore.studyFilterSearch;
    brokerParams = this.brokerAssocStore.brokerParams;
    selectedStudies = this.brokerAssocStore.selectedStudies;
    selectedExams = this.brokerAssocStore.selectedExams;
    exams = this.brokerAssocStore.exams;

    disableAssociateButton = computed(() => {
        return (
            this.studyFilterSearch()?.typeAssociation === EstensaModel.BrokerTypeFilter.TypeNotAsc &&
            (this.studies().length === 0 ? true : false)
        );
    });

    disableRemoveAssociationButton = computed(() => {
        return this.studyFilterSearch()?.typeAssociation !== EstensaModel.BrokerTypeFilter.TypeAscToPre;
    });

    canImages: Signal<boolean> = computed(() => {
        const curStudies = this.selectedStudies();
        if (!curStudies || curStudies.length != 1) return false;
        const curStudy: EstensaModel.BrokerDataStudy = curStudies[0];
        if (curStudy.modality.toUpperCase() == 'ECG') return false;
        return true;
    });

    checkSelected: Signal<boolean> = computed(() => {
        return this.checkSelectedItems(true, false) || this.checkSelectedItems(false, true);
    });

    /* Verifica che tra gli esami o studi selezionati, sia presente almeno un associato */
    checkAssociated: Signal<boolean | undefined> = computed(() => {
        const selectedStudies = this.selectedStudies();
        const selectedExams = this.selectedExams();
        const checkSelected = this.checkSelected();
        if (checkSelected) {
            var bExam = false;
            selectedExams?.forEach((item: EstensaModel.BrokerDataExam) => {
                if (item.associated) {
                    bExam = true;
                }
            });

            var bStudy = false;
            selectedStudies?.forEach((item: EstensaModel.BrokerDataStudy) => {
                if (item.associated) {
                    bStudy = true;
                }
            });

            return bStudy && bExam;
        }
    });

    check_Studies_OnCD: Signal<boolean> = computed(() => {
        const selectedStudies = this.selectedStudies();
        var bResult = false;
        selectedStudies?.forEach((item: EstensaModel.BrokerDataStudy) => {
            if (item.onLocalCD) {
                bResult = true;
            }
        });
        return bResult;
    });

    check_Studies_WorkInProgress: Signal<boolean> = computed(() => {
        const selectedStudies = this.selectedStudies();
        var bResult = false;
        selectedStudies?.forEach((item: EstensaModel.BrokerDataStudy) => {
            if (item.workInProgress) {
                bResult = true;
            }
        });
        return bResult;
    });

    canAssociate: Signal<boolean> = computed(() => {
        const checkSelected = this.checkSelected();
        const checkAssociated = this.checkAssociated();
        const check_Studies_OnCD = this.check_Studies_OnCD();
        const check_Studies_WorkInProgress = this.check_Studies_WorkInProgress();
        return checkSelected && !checkAssociated && !check_Studies_OnCD && !check_Studies_WorkInProgress;
    });

    checkAssociatedSearchType: Signal<boolean> = computed(() => {
        const studyFilterSearch = this.studyFilterSearch();
        const examsFilterSearch = this.brokerParams()?.searchExamData;
        if (
            examsFilterSearch?.typeAssociation == EstensaModel.BrokerTypeFilter.TypeAscToPre ||
            studyFilterSearch?.typeAssociation == EstensaModel.BrokerTypeFilter.TypeAscToPre
        )
            return true;
        return false;
    });

    canRemoveAssociation: Signal<boolean | undefined> = computed(() => {
        const checkSelected = this.checkSelected();
        const checkAssociated = this.checkAssociated();
        const checkAssociatedSearchType = this.checkAssociatedSearchType();
        const check_Studies_WorkInProgress = this.check_Studies_WorkInProgress();
        return checkSelected && checkAssociated && checkAssociatedSearchType && !check_Studies_WorkInProgress;
    });

    check_Exams_Validate: Signal<boolean> = computed(() => {
        const exams = this.exams();
        var bResult = false;
        exams.forEach((item: EstensaModel.BrokerDataExam) => {
            if (item.validate) {
                bResult = true;
            }
        });
        return bResult;
    });

    check_Studies_Suspect: Signal<boolean> = computed(() => {
        const selectedStudies = this.selectedStudies();
        var bResult = true;
        selectedStudies?.forEach((item: EstensaModel.BrokerDataStudy) => {
            if (!item.suspect) {
                bResult = false;
            }
        });
        return bResult;
    });

    canValidateAssociation: Signal<boolean> = computed(() => {
        const check_Exams_Validate = this.check_Exams_Validate();
        const check_Studies_Suspect = this.check_Studies_Suspect();
        return this.checkSelectedItems(true, false) && check_Exams_Validate && check_Studies_Suspect;
    });

    minDate: Moment;
    maxDate: Moment;

    dialogRef = inject(MatDialogRef<BrokerAssosiationModalComponent>);

    params = signal<EstensaModel.BrokerCheckParams>(this.data.origin.params);
    findStudyAssoc = signal<boolean>(this.data.findStudyAssoc);
    originExams = signal<EstensaModel.BrokerDataExam[]>(this.data.origin.exams);

    patientSearchForm: FormGroup = new FormGroup({
        cognome: new FormControl(null),
        nome: new FormControl(null),
        sesso: new FormControl(null),
        dataNascita: new FormControl(null),
        idPatient: new FormControl(null),
        issuer: new FormControl(null),
    });

    studiesSearchForm: FormGroup = new FormGroup({
        typology: new FormControl(EstensaModel.BrokerTypeFilter.TypeNotAsc),
        fromDate: new FormControl(),
        toDate: new FormControl(),
        modality: new FormControl(),
        description: new FormControl(null),
        top: new FormControl(200),
    });

    //ag-grid
    rowHeight: number = 43;
    autoSizeStrategy: SizeColumnsToFitGridStrategy = {
        type: 'fitGridWidth',
    };
    //dataSource = signal<BrokerExtendedTableDataDisplay[]>([]);

    dataSource: Signal<BrokerExtendedTableDataDisplay[]> = computed(() => {
        const exams = this.exams();
        return this.initDataSourceValue(exams);
    });

    noRowsTemplate: string = this.translateService.instant('se-ui-datagrid_no_row_overlay');
    rowSelection: RowSelectionOptions = {
        mode: 'multiRow',
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: true,
    };

    dataSourceSearchResult = computed(() => {
        return this.brokerAssocStore.studies().map((el) => {
            return {
                cognome: el.patientInfo.cognome,
                nome: el.patientInfo.nome,
                sesso: el.patientInfo.sesso,
                dataNascita: el.patientInfo.dataNascita,
                description: el.description,
                modality: el.modality,
                inBroker: el.inBroker,
                status: this.setStatus(el),
                date: el.date,
                study: el,
            };
        });
    });

    columnDefs: ColDef[] = [
        {
            field: 'status',
            cellRenderer: StatusRendererComponent,
            //valueFormatter: (params) => params.value.length ? this.translateService.instant(params.value) : BrokerAssociationStatus.DEFAULT,
            headerName: this.translateService.instant('Status'),
            tooltipValueGetter: (params) =>
                params.value.length ? this.translateService.instant(params.value) : BrokerAssociationStatus.DEFAULT,
            suppressSizeToFit: true,
            sortable: true,
            flex: 1,
            resizable: false,
        },
        {
            field: 'cognome',
            headerName: this.translateService.instant('LastName'),
            headerTooltip: this.translateService.instant('LastName'),
            tooltipField: 'cognome',
            suppressSizeToFit: true,
            sortable: true,
            flex: 1,
            resizable: false,
            sort: 'desc',
        },
        {
            field: 'nome',
            headerName: this.translateService.instant('FirstName'),
            headerTooltip: this.translateService.instant('FirstName'),
            tooltipField: 'nome',
            suppressSizeToFit: true,
            sortable: true,
            flex: 1,
            resizable: false,
        },
        {
            field: 'sesso',
            headerName: this.translateService.instant('Sex'),
            headerTooltip: this.translateService.instant('Sex'),
            tooltipField: 'sesso',
            suppressSizeToFit: true,
            sortable: true,
            flex: 1,
            resizable: false,
        },
        {
            field: 'dataNascita',
            headerName: this.translateService.instant('BirthDate'),
            headerTooltip: this.translateService.instant('BirthDate'),
            tooltipField: 'dataNascita',
            valueFormatter: (params) => {
                return moment(params.value).format('DD/MM/YYYY HH:mm');
            },
            suppressSizeToFit: true,
            sortable: true,
            flex: 1,
            resizable: false,
        },
        {
            field: 'modality',
            headerName: this.translateService.instant('Modality'),
            headerTooltip: this.translateService.instant('Modality'),
            tooltipField: 'modality',
            suppressSizeToFit: true,
            sortable: true,
            flex: 1,
            resizable: false,
        },
        {
            field: 'date',
            headerName: this.translateService.instant('EXAM_DATE'),
            headerTooltip: this.translateService.instant('EXAM_DATE'),
            tooltipField: 'date',
            valueFormatter: (params) => {
                return moment(params.value).format('DD/MM/YYYY HH:mm');
            },
            suppressSizeToFit: true,
            sortable: true,
            flex: 1,
            resizable: false,
        },
        {
            field: 'description',
            headerName: this.translateService.instant('Description'),
            headerTooltip: this.translateService.instant('Description'),
            tooltipField: 'description',
            suppressSizeToFit: true,
            sortable: true,
            flex: 1,
            resizable: false,
        },
        {
            field: 'inBroker',
            headerName: this.translateService.instant('InCharge'),
            headerTooltip: this.translateService.instant('InCharge'),
            tooltipField: 'inBroker',
            valueFormatter: (params) => {
                return moment(params.value).format('DD/MM/YYYY HH:mm');
            },
            suppressSizeToFit: true,
            sortable: true,
            flex: 1,
            resizable: false,
        },
    ];

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            origin: {
                params: EstensaModel.BrokerCheckParams;
                studies: EstensaModel.BrokerDataStudy[];
                exams: EstensaModel.BrokerDataExam[];
            };
            findStudyAssoc: boolean;
        },
    ) {
        this.brokerAssocStore.setCurrentManagementParams(this.params(), this.findStudyAssoc(), this.originExams());
        this.brokerAssocStore.loadExams(this.brokerAssocStore.brokerParams);
        this.brokerAssocStore.loadStudies(this.studyFilterSearch);

        const typology = this.studyFilterSearch()?.typeAssociation;
        //const fromDate = moment.utc(this.studyFilterSearch()?.fromDate).format('DD/MM/YYYY');
        //const toDate = moment.utc(this.studyFilterSearch()?.toDate).format('DD/MM/YYYY');
        const fromDate = this.studyFilterSearch()?.fromDate;
        const toDate = this.studyFilterSearch()?.toDate;
        const modality = this.studyFilterSearch()?.modality;

        this.studiesSearchForm.controls.typology.setValue(typology);
        this.studiesSearchForm.controls.fromDate.setValue(fromDate);
        this.studiesSearchForm.controls.toDate.setValue(toDate);
        this.studiesSearchForm.controls.modality.setValue(modality);
    }

    ngOnInit() {
        this.studiesSearchForm.get('fromDate')?.valueChanges.subscribe((value) => {
            this.minDate = moment(this.studyFilterSearch().fromDate);
        });
        this.studiesSearchForm.get('toDate')?.valueChanges.subscribe((value) => {
            this.maxDate = moment(this.studyFilterSearch().toDate);
        });
    }
    resetForms() {
        console.log('before studiesSearchForm', this.studiesSearchForm.controls);
        this.patientSearchForm.reset();
        this.studiesSearchForm.reset();
        console.log('after studiesSearchForm', this.studiesSearchForm.controls);

        this.studiesSearchForm.controls.top?.setValue(200);
        this.studiesSearchForm.controls.typology?.setValue(EstensaModel.BrokerTypeFilter.TypeNotAsc);
    }
    resetFormControl(formName: 'patient' | 'study', controlName: string) {
        if (formName == 'patient') {
            this.patientSearchForm.get(controlName)?.setValue(null);
        } else {
            if (controlName === 'typology')
                this.studiesSearchForm.get(controlName)?.setValue(EstensaModel.BrokerTypeFilter.All);
            if (controlName === 'description') this.studiesSearchForm.get(controlName)?.setValue('');
            if (controlName === 'fromDate') this.studiesSearchForm.get(controlName)?.setValue(null);
            if (controlName === 'toDate') this.studiesSearchForm.get(controlName)?.setValue(null);
            if (controlName === 'modality') this.studiesSearchForm.get(controlName)?.setValue(null);
            if (controlName === 'top') this.studiesSearchForm.get(controlName)?.setValue('');
        }
    }
    onValueChange(formName: 'patient' | 'study', controlName: string, value: any) {
        if (formName == 'patient') {
            this.patientSearchForm.get(controlName)?.setValue(value.value);
        } else {
            this.studiesSearchForm.get(controlName)?.setValue(value.value);
        }
    }

    onGridSizeChanged(sizeEvent: GridSizeChangedEvent) {
        let clientWidth = sizeEvent.clientWidth;
        let columns = sizeEvent.api.getAllDisplayedColumns();
        let usedWidth = sumBy(columns, (col) => col.getActualWidth());

        if (usedWidth < clientWidth) {
            this.resize(sizeEvent.api);
        }
        this.changeRowHeight(window.innerWidth, sizeEvent);
    }

    private changeRowHeight(screenWidth: number, sizeEvent: GridSizeChangedEvent): void {
        if (screenWidth > 1281 && screenWidth < 1440 && this.rowHeight !== 43) {
            this.rowHeight = 43;
            sizeEvent.api.resetRowHeights();
        } else if (screenWidth > 801 && screenWidth < 1280 && this.rowHeight !== 38) {
            this.rowHeight = 38;
            sizeEvent.api.resetRowHeights();
        } else if ((screenWidth > 1441 || screenWidth <= 801) && this.rowHeight !== 48) {
            this.rowHeight = 48;
            sizeEvent.api.resetRowHeights();
        }
    }

    onRowSelected(event: any, table: 'exams' | 'studies') {
        console.log('Selection element from: ', table);
        console.log('rowSelection', event);
        switch (table) {
            case 'exams':
                break;
            case 'studies':
                console.log('selectedStudies', this.selectedStudies());
                break;
        }
    }

    onSelectedItemsChange(evt: SelectionChangedEvent, table: 'exams' | 'studies') {
        console.log('SelectionChange from: ', table);
        const selectedData = evt.api.getSelectedRows();
        console.log('Selection Changed', selectedData);
        switch (table) {
            case 'exams':
                const exams = selectedData.map((el) => {
                    return el?.exam;
                });
                this.brokerAssocStore.setCurrentExams(exams);
                break;
            case 'studies':
                const studies = selectedData.map((el) => {
                    return el?.study;
                });
                console.log('studies', studies);
                this.brokerAssocStore.setCurrentStudies(studies);
                break;
        }
    }
    onFirstDataRendered(renderEvent: FirstDataRenderedEvent, table: 'exams' | 'studies') {
        switch (table) {
            case 'exams':
                if (this.dataSource().length === 1) {
                    let firstRow = renderEvent.api.getDisplayedRowAtIndex(0);
                    firstRow?.setSelected(true);
                }
                break;
            case 'studies':
                let firstRow = renderEvent.api.getDisplayedRowAtIndex(0);
                firstRow?.setSelected(true);
                break;
        }
    }

    private resize = (api: GridApi) => {
        api.sizeColumnsToFit();
    };
    private initDataSourceValue = (data: any): BrokerExtendedTableDataDisplay[] => {
        let array = data.length
            ? data.map((el: EstensaModel.BrokerDataExam) => {
                  return {
                      cognome: el.patientInfo.cognome,
                      nome: el.patientInfo.nome,
                      sesso: el.patientInfo.sesso,
                      dataNascita: el.patientInfo.dataNascita,
                      description: el.description,
                      modality: el.modality,
                      inBroker: el.inBroker,
                      status: this.setStatus(el),
                      date: el.date,
                      exam: el,
                  };
              })
            : [];
        console.log(array);
        return array;
    };

    setStatus(exam: any): string {
        let status = BrokerAssociationStatus.DEFAULT;
        if (exam.validate) {
            status = exam.suspect ? BrokerAssociationStatus.ASSOCIABLE : BrokerAssociationStatus.VALIDATED;
        }
        if (exam.conservabile) {
            status = BrokerAssociationStatus.ARCHIVED;
        }
        if (exam.workInProgress) {
            status = BrokerAssociationStatus.MODIFIED;
        }

        return status;
    }

    /**
     * Restituisce True se almeno un esame ed uno studio sono stati selezioni.
     * @param bSingleExam se è a true, la funzione restituisce true se è stato selezionato solo un esame
     * @param bSingleStudy se è a true, la funzione restituisce true se è stato selezionato solo uno studio
     */
    checkSelectedItems(bSingleExam: boolean, bSingleStudy: boolean) {
        const exams = this.exams();
        const selectedStudies = this.selectedStudies();
        if (!exams || !selectedStudies) return false;
        if (!exams.length || !selectedStudies.length) return false;
        if (exams.length == 0 || selectedStudies.length == 0) return false;
        if (bSingleExam && exams.length > 1) return false;
        if (bSingleStudy && selectedStudies.length > 1) return false;
        return true;
    }

    closeDialog(): void {
        this.dialogRef.close({ action: 'Cancel', data: this.data });
    }

    associateStudies() {
        this.brokerAssocStore.associateStudies();
    }

    validateAssociation() {
        this.brokerAssocStore.validateAssociation();
    }

    removeAssociation() {
        this.brokerAssocStore.removeAssociation();
    }

    openImages() {
        this.brokerAssocStore.onOpenViewer();
    }

    search() {
        let filterStudies = new EstensaModel.BrokerDataFilterStudy();
        filterStudies.setSearchByIdPresList(this.params(), this.studiesSearchForm.controls['typology'].value);
        filterStudies.description = this.studiesSearchForm.controls['description'].value;
        filterStudies.toDate = this.studiesSearchForm.controls['toDate'].value;
        filterStudies.fromDate = this.studiesSearchForm.controls['fromDate'].value;
        filterStudies.modality = this.studiesSearchForm.controls['modality'].value;
        filterStudies.patientFilter = this.patientSearchForm.value;
        filterStudies.top = this.studiesSearchForm.controls['top'].value;
        filterStudies.patientFilter.cognome = this.patientSearchForm.controls['cognome'].value;
        filterStudies.patientFilter.nome = this.patientSearchForm.controls['nome'].value;
        filterStudies.patientFilter.sesso = this.patientSearchForm.controls['sesso'].value;
        filterStudies.patientFilter.dataNascita = this.patientSearchForm.controls['dataNascita'].value;
        this.brokerAssocStore.setManualStudiesSearchParams(filterStudies);
    }
}
